import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import { PButton, PHeading, PText } from "@porsche-design-system/components-react"
import { spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import type { DocumentResponse, PriceTagView } from "@slfinrtl/ui-bff-routes"
import { generatePriceTagRoute, getPriceTagViewRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { commonMessages } from "src/common/messages/common.messages"
import { openPdf } from "src/common/pdf/openPdf"
import { ButtonGroup, Checkbox, FormLayout, FormLayoutGroup, RadioGroup, ValidationMessage } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { useBff } from "src/hooks/useBff/useBff"
import { useBffDynamic } from "src/hooks/useBffDynamic/useBffDynamic"
import { useConfig } from "src/hooks/useConfig/useConfig"
import { useLanguageOptions } from "src/hooks/useLanguageOptions/useLanguageOptions"
import { EquipmentList } from "./equipmentList/EquipmentList"
import { EquipmentListSkeleton } from "./equipmentList/EquipmentListSkeleton"

const messages = defineMessages({
	equipmentHeadline: {
		defaultMessage: "Choose equipment",
		id: "printPriceTag.equipment.headline",
	},
	equipmentSubheadline: {
		defaultMessage: "Choose the equipment that you would like to print.",
		id: "printPriceTag.subheadline",
	},
	headline: {
		defaultMessage: "Print pricetag",
		id: "printPriceTag.headline",
	},
	languageHeadline: {
		defaultMessage: "Choose Language",
		id: "printPriceTag.language.headline",
	},
	languageSubheadline: {
		defaultMessage: "Choose the language in which you want to print the documents.",
		id: "printPriceTag.language.subheadline",
	},
	missingTranslations: {
		defaultMessage: "Some of the selected options are not translated into the selected language.",
		id: "printPriceTag.error.missingTranslations",
	},
	withCrestLabel: {
		defaultMessage: "Print Porsche logo",
		id: "printPriceTag.crest.label",
	},
	withFsOffer: {
		defaultMessage: "Print Financial Offer",
		id: "printPriceTag.financialOffer.label",
	},
})

export interface PrintPriceTagModalContainerProps {
	vehicleKey: string
}

type Props = ModalProps<PrintPriceTagModalContainerProps>

export const PrintPriceTagModalContainer = withErrorHandler(({ id, modalProps }: Props) => {
	const { vehicleKey } = modalProps
	const { hideModal } = useContext(ModalContext)

	const { formatMessage } = useIntl()
	const { availableLocales, locale, marketplaceKey } = useDealerContext()
	const fetchEquipmentOptions = useBff(
		getPriceTagViewRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {
				"inventory-id": vehicleKey,
			},
		},
		"inline",
	)
	const printPriceTag = useBffDynamic(generatePriceTagRoute, "inline")
	const {
		print: {
			financialServices: { priceTagFsOfferEnabled },
		},
	} = useConfig()

	const hide = useCallback(() => hideModal(id), [hideModal, id])

	const [view, setView] = useState<PriceTagView>()
	const [isViewLoading, setIsViewLoading] = useState(false)
	const [isSelectedFinancialOffer, setIsSelectedFinancialOffer] = useState(priceTagFsOfferEnabled ? true : undefined)
	const [isSelectedWithCrest, setIsSelectedWithCrest] = useState(true)
	const [disableFinancialOfferCheckbox, setDisableFinancialOfferCheckbox] = useState<boolean>(false)
	const [selectedEquipment, setSelectedEquipment] = useState<string[]>([])
	const [selectedLocale, setSelectedLocale] = useState<Locale>()
	const [isPrinting, setIsPrinting] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string | undefined>()

	const equipmentItems = view?.equipments

	const selectedOptions = useMemo(() => {
		return equipmentItems ? equipmentItems.filter((item) => selectedEquipment.includes(item.code)) : []
	}, [equipmentItems, selectedEquipment])

	const selectedLocalizedOptions = useMemo(() => {
		if (!equipmentItems || !selectedLocale) {
			return []
		}

		return selectedEquipment.reduce<string[]>((acc, code) => {
			const selectedItem = equipmentItems.find((item) => item.code === code)
			const localizedName = selectedItem?.name?.[selectedLocale]

			if (localizedName) {
				acc.push(localizedName)
			}

			return acc
		}, [])
	}, [equipmentItems, selectedEquipment, selectedLocale])

	const hasMissingTranslations = selectedLocalizedOptions.length < selectedOptions.length

	const handleViewLoaded = useCallback(
		(response: PriceTagView) => {
			setView(response)

			// Select all Options on init.
			setSelectedEquipment(response.equipments.map((item) => item.code))

			// Preselect current locale if it exists in the map of available languages. Otherwise, select the first one.
			const preselectedLocale = availableLocales.find((language) => language === locale)
			setSelectedLocale(preselectedLocale ?? availableLocales[0])
		},
		[locale, availableLocales],
	)

	const handleEquipmentToggled = useCallback(
		(code: string) => {
			const newValue = [...selectedEquipment]
			const selectedIndex = selectedEquipment.indexOf(code)
			if (selectedIndex > -1) {
				newValue.splice(selectedIndex, 1)
			} else {
				newValue.push(code)
			}

			setSelectedEquipment(newValue)
		},
		[selectedEquipment],
	)

	useEffect(() => {
		setIsViewLoading(true)
		fetchEquipmentOptions()
			.then(handleViewLoaded)
			.finally(() => setIsViewLoading(false))
	}, [vehicleKey, handleViewLoaded, fetchEquipmentOptions])

	const handlePrintSuccess = useCallback(
		({ document }: DocumentResponse) => {
			openPdf(document)
			hide()
		},
		[hide],
	)

	const handleError = useCallback(
		(error: any) => {
			setErrorMessage(error?.response?.data?.message ?? error.message ?? formatMessage(formMessages.errorGeneral))
		},
		[formatMessage],
	)
	const onSubmit = useCallback(() => {
		if (!selectedLocale) {
			return
		}

		setIsPrinting(true)
		setErrorMessage(undefined)
		printPriceTag({
			body: { allowedOptionNames: selectedLocalizedOptions },
			pathParams: {
				inventoryId: vehicleKey,
				languageTag: selectedLocale,
			},
			queryParams: {
				withCrest: isSelectedWithCrest ? "true" : "false",
				withFsOffer: isSelectedFinancialOffer ? "true" : "false",
			},
		})
			.then(handlePrintSuccess)
			.catch(handleError)
			.finally(() => setIsPrinting(false))
	}, [
		selectedLocale,
		printPriceTag,
		selectedLocalizedOptions,
		vehicleKey,
		isSelectedWithCrest,
		isSelectedFinancialOffer,
		handlePrintSuccess,
		handleError,
	])

	const languageOptions = useLanguageOptions()

	const onLanguageSelected = useCallback(
		(language: string) => {
			if (marketplaceKey === "de" && language.startsWith("en")) {
				setIsSelectedFinancialOffer(false)
				setDisableFinancialOfferCheckbox(true)
			} else {
				setDisableFinancialOfferCheckbox(false)
			}

			setSelectedLocale(language as Locale)
		},
		[marketplaceKey],
	)

	return (
		<Modal id={id} heading={formatMessage(messages.headline)}>
			<StyledErrorNotificationInline />

			<FormLayoutGroup>
				<FormLayout>
					<Checkbox onChange={setIsSelectedWithCrest} checked={isSelectedWithCrest}>
						<FormattedMessage {...messages.withCrestLabel} />
					</Checkbox>

					{priceTagFsOfferEnabled && (
						<Checkbox
							onChange={setIsSelectedFinancialOffer}
							checked={isSelectedFinancialOffer}
							disabledHint={disableFinancialOfferCheckbox ? "" : undefined}
						>
							<FormattedMessage {...messages.withFsOffer} />
						</Checkbox>
					)}
				</FormLayout>

				{availableLocales && Object.keys(availableLocales).length > 1 && (
					<FormLayout>
						<div>
							<PHeading size="md">
								<FormattedMessage {...messages.languageHeadline} />
							</PHeading>
							<PText>
								<FormattedMessage {...messages.languageSubheadline} />
							</PText>
						</div>
						<RadioGroup
							name="language"
							value={selectedLocale}
							onChange={onLanguageSelected}
							required
							options={languageOptions}
						/>
						{hasMissingTranslations && (
							<StyledValidationMessage message={formatMessage(messages.missingTranslations)} />
						)}
					</FormLayout>
				)}

				<FormLayout>
					<div>
						<PHeading size="md">
							<FormattedMessage {...messages.equipmentHeadline} />
						</PHeading>
						<PText>
							<FormattedMessage {...messages.equipmentSubheadline} />
						</PText>
					</div>
					{equipmentItems && selectedLocale && !isViewLoading ? (
						<EquipmentList
							items={equipmentItems}
							selectedLocale={selectedLocale}
							selectedEquipment={selectedEquipment}
							onEquipmentToggled={handleEquipmentToggled}
						/>
					) : (
						<EquipmentListSkeleton />
					)}
				</FormLayout>
			</FormLayoutGroup>

			<ButtonGroup>
				<PButton loading={isPrinting} onClick={onSubmit}>
					<FormattedMessage {...commonMessages.print} />
				</PButton>
				<PButton type="button" variant="secondary" disabled={isPrinting} onClick={hide}>
					<FormattedMessage {...formMessages.cancel} />
				</PButton>
			</ButtonGroup>
			{errorMessage && <StyledValidationMessage message={errorMessage} />}
		</Modal>
	)
})

const StyledErrorNotificationInline = styled(ErrorNotificationInline)`
	margin-top: 0;
`

const StyledValidationMessage = styled(ValidationMessage)`
  margin-top: ${spacingStaticSm};
`
